import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from '../../services/api';
import { useCreditConfig } from '../../contexts/CreditConfigContext';
import { useAuthCheck } from '../../hooks/useAuthCheck';
import { useI18n } from '../../contexts/I18nContext';
import { usePolling } from '../../hooks/usePolling';
import { handleUnifiedError, showSuccessToast } from '../../utils/unifiedErrorHandler';
import TextToImagePanelView from './TextToImagePanelView';
import './TextToImagePanel.scss';

const TextToImagePanel = () => {
  const { t } = useI18n();
  const location = useLocation();
  const [prompt, setPrompt] = useState('');
  const [generatedImage, setGeneratedImage] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [selectedStyle, setSelectedStyle] = useState('');
  const [selectedSize, setSelectedSize] = useState('Auto');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [updateRecordId, setUpdateRecordId] = useState(null); // 用于更新现有记录的ID
  const [shareOpen, setShareOpen] = useState(false);
  const [shareMediaUrl, setShareMediaUrl] = useState('');
  
  // 使用积分配置上下文
  const { getToolDefaultCost, getImageGenerationCredits } = useCreditConfig();
  const creditsPerImage = getImageGenerationCredits({
    toolId: 'text-to-image',
    model: selectedSize && typeof selectedSize === 'object' ? selectedSize.model : undefined,
    resolution: selectedSize && typeof selectedSize === 'object' ? selectedSize.resolution : undefined
  }) || getToolDefaultCost();

  // 使用统一的认证检查Hook
  const { checkAuthAndExecute, showLoginModal, handleCloseLogin } = useAuthCheck();

  // 使用公共轮询Hook
  const { startPolling, stopPolling } = usePolling({
    maxAttempts: 60,
    pollInterval: 5000,
    initialDelay: 2000,
    onSuccess: (data) => {
      setGenerationProgress(100);
      setTimeout(() => {
        setGeneratedImage({ url: data.imageUrl, id: data.id });
        setShareMediaUrl(data.shareUrl || '');
        setIsGenerating(false);
        setGenerationProgress(0);
      }, 500);
      showSuccessToast(t('aiWorkbench.generateSuccess'));
    },
    onFailed: (result) => {
      setIsGenerating(false);
      setGenerationProgress(0);
      // usePolling已经显示了错误toast，这里不需要重复显示
    },
    onError: () => {
      setIsGenerating(false);
      setGenerationProgress(0);
    },
    onTimeout: () => {
      setIsGenerating(false);
      setGenerationProgress(0);
    }
  });

  // 超时保护机制：防止进度条无限卡住
  useEffect(() => {
    let timeoutId;
    if (isGenerating) {
      // 设置5分钟超时，强制重置进度条
      timeoutId = setTimeout(() => {
        console.warn('Image generation timeout after 5 minutes, forcing reset');
        setIsGenerating(false);
        setGenerationProgress(0);
        stopPolling();
        handleUnifiedError(new Error(t('aiToolTemplate.generationTimeout')), t, {
          showToast: true,
          severity: 'warning'
        });
      }, 60000);
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isGenerating, stopPolling, t]);

  const styles = [
    { id: 'realistic', name: t('aiWorkbench.styles.realistic'), description: t('aiWorkbench.styleDescriptions.realistic') },
    { id: 'artistic', name: t('aiWorkbench.styles.artistic'), description: t('aiWorkbench.styleDescriptions.artistic') },
    { id: 'anime', name: t('aiWorkbench.styles.anime'), description: t('aiWorkbench.styleDescriptions.anime') },
    { id: 'cartoon', name: t('aiWorkbench.styles.cartoon'), description: t('aiWorkbench.styleDescriptions.cartoon') },
    { id: 'abstract', name: t('aiWorkbench.styles.abstract'), description: t('aiWorkbench.styleDescriptions.abstract') }
  ];

  // Handle URL parameters for pre-filling prompt and update record ID
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const promptParam = urlParams.get('prompt');
    const updateRecordIdParam = urlParams.get('updateRecordId');
    
    if (promptParam) {
      setPrompt(decodeURIComponent(promptParam));
    }
    
    if (updateRecordIdParam) {
      setUpdateRecordId(updateRecordIdParam);
    }
  }, [location.search]);

  const handleGenerateImage = async () => {
    if (!prompt.trim()) {
      handleUnifiedError(new Error(t('aiWorkbench.enterPromptError')), t, {
        showToast: true,
        severity: 'warning'
      });
      return;
    }

    // 使用统一的认证检查
    checkAuthAndExecute(async () => {
      setIsGenerating(true);
      setGenerationProgress(0);
      
      try {
        let finalPrompt = prompt.trim();
        if (selectedStyle) {
          const styleObj = styles.find(s => s.id === selectedStyle);
          if (styleObj && styleObj.description) {
             // 如果提示词不为空，添加逗号分隔
             if (finalPrompt) {
               finalPrompt = `${finalPrompt}, ${styleObj.description}`;
             } else {
               finalPrompt = styleObj.description;
             }
          }
        }
        
        let aspectRatio = selectedSize || 'Auto';
        let model = 'nano';
        let resolution;
        if (selectedSize && typeof selectedSize === 'object') {
          aspectRatio = selectedSize.aspectRatio || 'Auto';
          model = selectedSize.model || model;
          resolution = selectedSize.resolution;
        }
        if (aspectRatio && typeof aspectRatio === 'object') {
          aspectRatio = aspectRatio.aspectRatio || 'Auto';
        }
        const requestData = {
          prompt: finalPrompt,
          style: selectedStyle,
          aspectRatio,
          resolution,
          toolId: 'text-to-image',
          model
        };
        
        // Add updateRecordId if we're updating an existing record
        if (updateRecordId) {
          requestData.updateRecordId = updateRecordId;
        }
        
        const response = await axios.post('/generate-image', requestData);
        const data = response.data;
        const generationId = data.id;
      
        // 使用公共轮询Hook开始轮询
        startPolling(generationId, (progress) => {
          setGenerationProgress(progress);
        });
      } catch (error) {
        // 确保在任何错误情况下都重置进度条和生成状态
        setIsGenerating(false);
        setGenerationProgress(0);
        
        // 停止可能正在进行的轮询
        stopPolling();
        
        console.error('Image generation failed:', error);
        handleUnifiedError(error, t, {
          showToast: true,
          fallbackMessage: t('aiWorkbench.generationFailed')
        });
      }
    });
  };

  return (
    <TextToImagePanelView
      t={t}
      prompt={prompt}
      setPrompt={setPrompt}
      styles={styles}
      selectedStyle={selectedStyle}
      setSelectedStyle={setSelectedStyle}
      selectedSize={selectedSize}
      setSelectedSize={setSelectedSize}
      generationCount={1}
      creditsPerImage={creditsPerImage}
      isGenerating={isGenerating}
      isLoadingModels={false}
      generationProgress={generationProgress}
      handleGenerateImage={handleGenerateImage}
      generatedImage={generatedImage}
      shareOpen={shareOpen}
      setShareOpen={setShareOpen}
      shareMediaUrl={shareMediaUrl}
      showLoginModal={showLoginModal}
      handleCloseLogin={handleCloseLogin}
    />
  );
};

export default TextToImagePanel;
